export type AddressParts = {
  street?: string | null;
  unit?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  /** The landlord hid the address. Pass it only on renter-facing surfaces; owners always see the full address. */
  hideStreet?: boolean | null;
};

export const UNDISCLOSED_ADDRESS = '(Undisclosed address)';

export type AddressFormat = 'full' | 'street';

const clean = (value?: string | null): string => (value ?? '').trim();

const BARE_PART = String.raw`(?:\d+[a-z]?|[a-z]\d*)`;
const BARE_IDENTIFIER = new RegExp(`^${BARE_PART}(?:-${BARE_PART})*$`, 'i');

/**
 * Formats a unit per content design without rewriting what the user typed. A bare identifier gets
 * the prefix, keeping the case as typed: `#4` or `#b` for a single number or letter, `Unit 4B` otherwise.
 * A bare identifier is a number with an optional letter (`12A`), a letter with optional digits
 * (`B2`), or those joined by hyphens (`A-1`). Anything with words, glued or not (`Apt 4`, `Apt4`, `Upper`,
 * `2nd floor`), is shown exactly as typed. A typed
 * leading `#` is always dropped, since it comes from the old input adornment (`#5b` read `##5b`).
 * Returns an empty string when there is no unit.
 */
export function formatUnit(unit?: string | null): string {
  const value = clean(unit).replace(/^#+\s*/, '');
  if (!value) return '';
  if (!BARE_IDENTIFIER.test(value)) return value;

  return /^(\d+|[a-z])$/i.test(value) ? `#${value}` : `Unit ${value}`;
}

/**
 * Street and unit only, with no comma between them: `123 Main St Unit 4B`. A hidden address reads
 * `(Undisclosed address)`, sentence case per Constellation.
 */
export function formatStreetLine({ street, unit, hideStreet }: AddressParts): string {
  if (hideStreet) return UNDISCLOSED_ADDRESS;
  return [clean(street), formatUnit(unit)].filter(Boolean).join(' ');
}

/** City, state and ZIP: `Seattle, WA 98101`. */
export function formatCityLine({ city, state, zip }: AddressParts): string {
  const region = [clean(state), clean(zip)].filter(Boolean).join(' ');
  return [clean(city), region].filter(Boolean).join(', ');
}

/**
 * The address on one line. `full` (default): `123 Main St Unit 4B, Seattle, WA 98101`.
 * `street`: `123 Main St Unit 4B`, only where the city is already shown or space is tight.
 */
export function formatAddress(parts: AddressParts, format: AddressFormat = 'full'): string {
  const streetLine = formatStreetLine(parts);
  if (format === 'street') return streetLine;
  return [streetLine, formatCityLine(parts)].filter(Boolean).join(', ');
}

/** The address on two lines, for mailing-style blocks: `['123 Main St Unit 4B', 'Seattle, WA 98101']`. */
export function formatAddressLines(parts: AddressParts): [string, string] {
  return [formatStreetLine(parts), formatCityLine(parts)];
}
